import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CaseStudyCard from './components/CaseStudyCard';
import FilterPanel from './components/FilterPanel';
import TransformationTimeline from './components/TransformationTimeline';
import CaseStudyModal from './components/CaseStudyModal';
import SimilarCompanyFinder from './components/SimilarCompanyFinder';
import StatsOverview from './components/StatsOverview';

const CaseStudiesProof = () => {
  const [filters, setFilters] = useState({
    industry: '',
    companySize: '',
    useCase: '',
    transformationType: ''
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredCaseStudies, setFilteredCaseStudies] = useState([]);

  // Mock case studies data
  const caseStudies = [
    {
      id: 1,
      company: "MedTech Solutions",
      industry: "Healthcare",
      companySize: "Enterprise",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=600&fit=crop",
      challenge: "Legacy AI infrastructure with high vendor costs and limited customization capabilities for patient data analysis.",
      subtitle: "Transforming Healthcare AI with Open-Source Innovation",
      timeline: "12 months",
      keyMetrics: [
        { value: "68%", label: "Cost Reduction" },
        { value: "45%", label: "Faster Processing" },
        { value: "99.7%", label: "Uptime" }
      ],
      tags: ["TensorFlow", "PyTorch", "Kubernetes", "HIPAA Compliance", "MLOps", "Data Privacy"],
      useCase: "Platform Migration",
      transformationType: "Full Migration",
      detailedChallenge: `MedTech Solutions was struggling with escalating costs from their proprietary AI platform, spending over $2.4M annually on licensing fees alone. The platform's rigid architecture prevented customization needed for specialized medical imaging analysis, while vendor-imposed limitations restricted their ability to innovate and respond to changing healthcare regulations. Performance bottlenecks during peak usage periods were affecting patient care delivery, and the lack of transparency in AI decision-making processes raised compliance concerns with healthcare regulators.`,
      detailedSolution: `We implemented a comprehensive migration strategy using our AI Core Blueprint methodology. The solution involved deploying a cloud-native open-source AI platform built on TensorFlow and PyTorch, with Kubernetes orchestration for scalability. We established MLOps pipelines for continuous model deployment and monitoring, implemented HIPAA-compliant data handling procedures, and created custom medical imaging analysis models optimized for their specific use cases. The entire infrastructure was designed with transparency and auditability in mind to meet healthcare compliance requirements.`,
      detailedResults: `The transformation delivered exceptional results across all key performance indicators. MedTech Solutions achieved a 68% reduction in AI infrastructure costs, saving $1.6M annually while gaining complete control over their AI capabilities. Model performance improved by 45% through custom optimization, and system uptime increased to 99.7% with improved disaster recovery capabilities. Most importantly, they gained the flexibility to rapidly develop and deploy new AI models in response to emerging healthcare needs, positioning them as an innovation leader in their market.`,
      implementationSteps: [
        {
          title: "Infrastructure Assessment",
          description: "Comprehensive analysis of existing AI workloads and dependencies"
        },
        {
          title: "Architecture Design",
          description: "Custom open-source platform architecture with HIPAA compliance"
        },
        {
          title: "Phased Migration",
          description: "Risk-free migration of critical AI workloads with zero downtime"
        },
        {
          title: "Team Training",
          description: "Comprehensive training program for internal AI capabilities"
        }
      ],
      testimonial: {
        quote: "AI Clean Core didn't just migrate our AI infrastructure—they transformed our entire approach to healthcare innovation. We now have the freedom to develop cutting-edge AI solutions that directly improve patient outcomes.",author: "Dr. Sarah Chen",role: "Chief Technology Officer",avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face"
      }
    },
    {
      id: 2,
      company: "Global Financial Corp",industry: "Financial Services",companySize: "Enterprise",image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop",challenge: "Proprietary AI platform limitations hindering real-time fraud detection and regulatory compliance requirements.",subtitle: "Achieving Financial AI Independence and Regulatory Excellence",timeline: "10 months",
      keyMetrics: [
        { value: "72%", label: "Cost Savings" },
        { value: "3x", label: "Faster Detection" },
        { value: "100%", label: "Compliance" }
      ],
      tags: ["Apache Spark", "Kafka", "Docker", "SOC 2", "Real-time Analytics", "Fraud Detection"],
      useCase: "Vendor Independence",
      transformationType: "Hybrid Approach",
      detailedChallenge: `Global Financial Corp was locked into a proprietary AI platform that charged premium rates for financial services compliance features. The platform's black-box algorithms made it impossible to explain AI decisions to regulators, creating significant compliance risks. Real-time fraud detection was limited by the vendor's processing capabilities, resulting in delayed responses to emerging threats. The annual licensing costs of $3.2M were escalating by 15% yearly, with no guarantee of feature improvements or regulatory updates.`,
      detailedSolution: `Our solution centered on building a transparent, compliant AI infrastructure using Apache Spark for distributed processing and Kafka for real-time data streaming. We developed custom fraud detection models with full explainability features, implemented comprehensive audit trails for regulatory compliance, and created a hybrid cloud architecture that maintained data sovereignty while enabling global operations. The platform was designed with SOC 2 compliance from the ground up, ensuring the highest security standards.`,
      detailedResults: `The transformation exceeded all expectations, delivering 72% cost savings while dramatically improving fraud detection capabilities. Real-time processing speeds increased by 300%, enabling immediate response to fraudulent activities. The transparent AI models passed all regulatory audits with flying colors, and the organization gained complete control over their AI roadmap. The internal team now manages and continuously improves their AI capabilities, driving innovation at the speed of business rather than vendor release cycles.`,
      implementationSteps: [
        {
          title: "Regulatory Analysis",description: "Comprehensive review of compliance requirements and audit trails"
        },
        {
          title: "Real-time Architecture",description: "High-performance streaming platform for fraud detection"
        },
        {
          title: "Model Transparency",description: "Explainable AI implementation for regulatory compliance"
        },
        {
          title: "Security Hardening",description: "SOC 2 compliance and advanced security measures"
        }
      ],
      testimonial: {
        quote: "The transformation gave us something invaluable—complete transparency and control over our AI systems. We can now explain every decision to regulators and adapt instantly to new threats.",author: "Michael Rodriguez",role: "Head of Risk Management",avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
      }
    },
    {
      id: 3,
      company: "InnovateTech Manufacturing",industry: "Manufacturing",companySize: "Mid-Market",image: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=800&h=600&fit=crop",challenge: "Expensive proprietary AI solutions for predictive maintenance with limited customization for specialized equipment.",subtitle: "Revolutionizing Manufacturing with Custom AI Solutions",timeline: "8 months",
      keyMetrics: [
        { value: "55%", label: "Maintenance Costs" },
        { value: "89%", label: "Prediction Accuracy" },
        { value: "24%", label: "Uptime Increase" }
      ],
      tags: ["Scikit-learn", "IoT Integration", "Edge Computing", "Predictive Analytics", "Custom Models"],
      useCase: "Cost Optimization",
      transformationType: "Greenfield Implementation",
      detailedChallenge: `InnovateTech Manufacturing was paying $800K annually for a proprietary predictive maintenance platform that couldn't adapt to their unique equipment configurations. The one-size-fits-all approach resulted in frequent false positives and missed critical maintenance windows. Integration with their existing IoT sensors required expensive custom connectors, and the vendor's rigid data formats prevented them from leveraging their historical maintenance data effectively.`,
      detailedSolution: `We developed a custom predictive maintenance solution using scikit-learn and TensorFlow, specifically trained on their equipment data and maintenance patterns. The solution included edge computing capabilities for real-time monitoring, seamless IoT sensor integration, and custom algorithms optimized for their specific machinery types. We implemented a comprehensive data pipeline that incorporated 15 years of historical maintenance data, creating highly accurate predictive models tailored to their operations.`,
      detailedResults: `The custom solution delivered remarkable improvements across all operational metrics. Maintenance costs decreased by 55% through precise prediction of equipment failures, while prediction accuracy reached 89%—significantly higher than the previous proprietary solution. Equipment uptime increased by 24%, directly impacting production capacity and revenue. The organization now has complete control over their predictive maintenance capabilities and can rapidly adapt to new equipment or changing operational conditions.`,
      implementationSteps: [
        {
          title: "Data Integration",description: "Comprehensive integration of IoT sensors and historical data"
        },
        {
          title: "Custom Model Development",description: "Equipment-specific predictive models with high accuracy"
        },
        {
          title: "Edge Deployment",description: "Real-time processing capabilities at the equipment level"
        },
        {
          title: "Continuous Learning",description: "Self-improving models that adapt to changing conditions"
        }
      ],
      testimonial: {
        quote: "Our custom AI solution understands our equipment better than any vendor ever could. We've transformed from reactive maintenance to predictive excellence, and our bottom line shows it.",
        author: "Jennifer Walsh",
        role: "Operations Director",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face"
      }
    },
    {
      id: 4,
      company: "RetailMax Chain",
      industry: "Retail",
      companySize: "Enterprise",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=600&fit=crop",
      challenge: "Vendor lock-in with expensive AI recommendation engine limiting personalization capabilities and customer experience.",
      subtitle: "Personalizing Retail Experience with Independent AI",
      timeline: "6 months",
      keyMetrics: [
        { value: "43%", label: "Revenue Increase" },
        { value: "67%", label: "Cost Reduction" },
        { value: "85%", label: "Customer Satisfaction" }
      ],
      tags: ["Recommendation Systems", "Real-time Personalization", "A/B Testing", "Customer Analytics"],
      useCase: "Performance Enhancement",
      transformationType: "Full Migration",
      detailedChallenge: `RetailMax Chain was trapped in a costly vendor relationship, paying $1.8M annually for a recommendation engine that couldn't adapt to their unique customer segments and seasonal patterns. The black-box system provided no insights into customer behavior, and customization requests took months to implement through the vendor's development queue. Performance during peak shopping periods was inconsistent, directly impacting sales and customer satisfaction.`,
      detailedSolution: `We built a comprehensive personalization platform using open-source recommendation algorithms, real-time customer behavior analysis, and advanced segmentation capabilities. The solution included A/B testing frameworks for continuous optimization, real-time inventory integration for accurate recommendations, and detailed analytics dashboards for business insights. The platform was designed to handle peak traffic loads with auto-scaling capabilities and sub-second response times.`,
      detailedResults: `The transformation delivered exceptional business results with a 43% increase in revenue from improved personalization and a 67% reduction in AI infrastructure costs. Customer satisfaction scores increased to 85% due to more relevant product recommendations and faster page load times. The internal team now has complete visibility into customer behavior patterns and can implement new personalization strategies within days rather than months.`,
      implementationSteps: [
        {
          title: "Customer Data Analysis",
          description: "Deep analysis of customer behavior and purchase patterns"
        },
        {
          title: "Recommendation Engine",
          description: "Custom algorithms optimized for retail personalization"
        },
        {
          title: "Real-time Integration",
          description: "Seamless integration with inventory and customer systems"
        },
        {
          title: "Performance Optimization",
          description: "High-performance architecture for peak traffic handling"
        }
      ],
      testimonial: {
        quote: "The new AI platform doesn't just recommend products—it understands our customers. We've seen unprecedented engagement and sales growth since the transformation.",
        author: "David Kim",
        role: "Chief Digital Officer",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
      }
    },
    {
      id: 5,
      company: "EduTech University",
      industry: "Education",
      companySize: "Mid-Market",
      image: "https://images.unsplash.com/photo-1523050854058-8df90110c9d1?w=800&h=600&fit=crop",
      challenge: "Proprietary learning analytics platform with high costs and limited customization for diverse educational programs.",
      subtitle: "Democratizing Educational AI for Student Success",
      timeline: "9 months",
      keyMetrics: [
        { value: "78%", label: "Cost Savings" },
        { value: "92%", label: "Student Engagement" },
        { value: "35%", label: "Retention Improvement" }
      ],
      tags: ["Learning Analytics", "Student Success", "Adaptive Learning", "Educational Data Mining"],
      useCase: "Capability Building",
      transformationType: "Legacy Modernization",
      detailedChallenge: `EduTech University was spending $650K annually on a proprietary learning analytics platform that couldn't adapt to their diverse program structures and student populations. The system's rigid reporting capabilities didn't provide actionable insights for faculty, and integration with their existing learning management system was problematic. Student privacy concerns and limited customization options were hindering their ability to implement innovative educational approaches.`,
      detailedSolution: `We developed a comprehensive educational AI platform using open-source machine learning frameworks, designed specifically for higher education environments. The solution included adaptive learning algorithms, predictive analytics for student success, privacy-preserving data analysis techniques, and customizable dashboards for faculty and administrators. We implemented FERPA-compliant data handling and created specialized models for different academic disciplines and learning styles.`,
      detailedResults: `The transformation achieved remarkable improvements in both cost efficiency and educational outcomes. The university saved 78% on AI infrastructure costs while dramatically improving student engagement to 92% through personalized learning experiences. Most importantly, student retention improved by 35% through early intervention capabilities that identify at-risk students and recommend targeted support strategies. Faculty now have powerful tools to understand and improve student learning outcomes.`,
      implementationSteps: [
        {
          title: "Educational Data Integration",
          description: "Comprehensive integration of learning management and student systems"
        },
        {
          title: "Adaptive Learning Models",
          description: "Personalized learning algorithms for diverse student populations"
        },
        {
          title: "Privacy Implementation",
          description: "FERPA-compliant data handling and student privacy protection"
        },
        {
          title: "Faculty Training",
          description: "Comprehensive training program for educational AI adoption"
        }
      ],
      testimonial: {
        quote: "Our new AI platform has revolutionized how we understand and support student success. We can now provide truly personalized education at scale while maintaining complete data privacy.",
        author: "Dr. Maria Santos",
        role: "Vice Provost for Academic Innovation",
        avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face"
      }
    },
    {
      id: 6,
      company: "CloudTech Solutions",
      industry: "Technology",
      companySize: "SMB",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=600&fit=crop",
      challenge: "Small tech company struggling with expensive AI platform costs that were consuming 40% of their technology budget.",
      subtitle: "Empowering Small Tech Companies with Enterprise-Grade AI",
      timeline: "4 months",
      keyMetrics: [
        { value: "82%", label: "Cost Reduction" },
        { value: "3x", label: "Model Performance" },
        { value: "90%", label: "Development Speed" }
      ],
      tags: ["Startup AI", "Cost Optimization", "Rapid Deployment", "Scalable Architecture"],
      useCase: "Cost Optimization",
      transformationType: "Full Migration",
      detailedChallenge: `CloudTech Solutions, a growing SaaS company, was spending $240K annually on proprietary AI services—nearly 40% of their entire technology budget. The high costs were limiting their ability to invest in product development and market expansion. The vendor's pricing model penalized growth, making it financially unsustainable to scale their AI-powered features. Limited customization options were preventing them from differentiating their product in a competitive market.`,
      detailedSolution: `We implemented a cost-effective open-source AI infrastructure specifically designed for growing technology companies. The solution included containerized AI services, auto-scaling capabilities, and efficient resource utilization to minimize operational costs. We developed custom models optimized for their specific use cases and implemented CI/CD pipelines for rapid deployment and iteration. The architecture was designed to scale seamlessly with their business growth without proportional cost increases.`,
      detailedResults: `The transformation delivered exceptional value for the growing company, achieving 82% cost reduction while dramatically improving AI capabilities. Model performance increased by 300% through custom optimization, and development speed improved by 90% through streamlined deployment processes. The cost savings enabled CloudTech to reinvest in product development and accelerate their market expansion, while maintaining enterprise-grade AI capabilities that differentiate their offering.`,
      implementationSteps: [
        {
          title: "Cost Analysis",
          description: "Detailed analysis of current AI spending and optimization opportunities"
        },
        {
          title: "Efficient Architecture",
          description: "Cost-optimized infrastructure with auto-scaling capabilities"
        },
        {
          title: "Custom Development",
          description: "Tailored AI models for competitive differentiation"
        },
        {
          title: "Growth Planning",
          description: "Scalable architecture that grows with the business"
        }
      ],
      testimonial: {
        quote: "AI Clean Core transformed our AI costs from a growth limitation into a competitive advantage. We now have enterprise-grade capabilities at a fraction of the cost.",
        author: "Alex Thompson",
        role: "Founder & CTO",
        avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face"
      }
    }
  ];

  useEffect(() => {
    const filtered = caseStudies?.filter(caseStudy => {
      return ((filters?.industry === '' || caseStudy?.industry === filters?.industry) &&
      (filters?.companySize === '' || caseStudy?.companySize === filters?.companySize) &&
      (filters?.useCase === '' || caseStudy?.useCase === filters?.useCase) && (filters?.transformationType === '' || caseStudy?.transformationType === filters?.transformationType));
    });
    setFilteredCaseStudies(filtered);
  }, [filters]);

  const handleFilterChange = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      industry: '',
      companySize: '',
      useCase: '',
      transformationType: ''
    });
  };

  const handleViewDetails = (caseStudy) => {
    setSelectedCaseStudy(caseStudy);
    setIsModalOpen(true);
  };

  const handleFindSimilar = (profile) => {
    // Filter case studies based on profile
    const matchingCaseStudies = caseStudies?.filter(caseStudy => {
      const industryMatch = profile?.industry === '' || caseStudy?.industry?.toLowerCase()?.includes(profile?.industry);
      const sizeMatch = profile?.companySize === '' || caseStudy?.companySize?.toLowerCase()?.includes(profile?.companySize);
      return industryMatch && sizeMatch;
    });

    // Update filters to show matching results
    if (profile?.industry) {
      const industryMap = {
        'healthcare': 'Healthcare',
        'financial': 'Financial Services',
        'manufacturing': 'Manufacturing',
        'retail': 'Retail',
        'technology': 'Technology',
        'education': 'Education'
      };
      setFilters(prev => ({ ...prev, industry: industryMap?.[profile?.industry] || '' }));
    }

    // Scroll to results
    document.getElementById('case-studies-grid')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Case Studies & Proof - AI Clean Core</title>
        <meta name="description" content="Discover how organizations achieved AI independence with measurable results. Real transformation stories, quantified outcomes, and proven methodologies." />
      </Helmet>
      <Header />
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-brand-primary via-brand-primary/95 to-brand-primary/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full text-white text-sm font-medium mb-6">
              <Icon name="Award" size={16} className="mr-2" />
              150+ Successful Transformations
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Proven AI Independence
              <span className="block text-brand-secondary">Success Stories</span>
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
              Real organizations, measurable results, complete AI transformation. 
              See how companies across industries broke free from vendor lock-in 
              and achieved true AI independence with our proven methodology.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="secondary"
                size="lg"
                iconName="Play"
                iconPosition="left"
                className="bg-white text-brand-primary hover:bg-gray-50"
              >
                Watch Success Stories
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Download"
                iconPosition="left"
                className="border-white text-white hover:bg-white/10"
              >
                Download Case Study Pack
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Stats Overview */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <StatsOverview />
        </div>
      </section>
      {/* Case Studies Grid */}
      <section id="case-studies-grid" className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-text-primary mb-4">
              Transformation Success Stories
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              Explore detailed case studies from organizations that achieved AI independence. 
              Filter by industry, company size, or transformation type to find relevant examples.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filter Panel */}
            <div className="lg:w-80 flex-shrink-0">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                isOpen={isFilterOpen}
                onToggle={() => setIsFilterOpen(!isFilterOpen)}
              />
            </div>

            {/* Case Studies Grid */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-6">
                <div className="text-text-secondary">
                  Showing {filteredCaseStudies?.length} of {caseStudies?.length} case studies
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-text-secondary">Sort by:</span>
                  <select className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-brand-primary focus:border-transparent">
                    <option>Most Recent</option>
                    <option>Industry</option>
                    <option>Company Size</option>
                    <option>Cost Savings</option>
                  </select>
                </div>
              </div>

              {filteredCaseStudies?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {filteredCaseStudies?.map((caseStudy) => (
                    <CaseStudyCard
                      key={caseStudy?.id}
                      caseStudy={caseStudy}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="Search" size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold text-text-primary mb-2">
                    No matching case studies found
                  </h3>
                  <p className="text-text-secondary mb-6">
                    Try adjusting your filters to see more results
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    iconName="RefreshCw"
                    iconPosition="left"
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* Transformation Timeline */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TransformationTimeline />
        </div>
      </section>
      {/* Similar Company Finder */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SimilarCompanyFinder onFindSimilar={handleFindSimilar} />
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-brand-primary to-brand-primary/90">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join 150+ organizations that achieved AI independence. 
            Start your transformation with a comprehensive assessment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              className="bg-white text-brand-primary hover:bg-gray-50"
            >
              Schedule Assessment
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="FileText"
              iconPosition="left"
              className="border-white text-white hover:bg-white/10"
            >
              Get AI Core Blueprint
            </Button>
          </div>
        </div>
      </section>
      {/* Case Study Modal */}
      <CaseStudyModal
        caseStudy={selectedCaseStudy}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      {/* Footer */}
      <footer className="bg-text-primary text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                </div>
                <div>
                  <div className="text-xl font-bold">AI Clean Core</div>
                  <div className="text-sm text-gray-400">Enterprise AI Independence</div>
                </div>
              </div>
              <p className="text-gray-400 mb-4">
                Helping organizations achieve true AI independence through open-source solutions 
                and proven methodologies. Break free from vendor lock-in and build sustainable AI capabilities.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/ai-core-blueprint" className="hover:text-white transition-colors">AI Core Blueprint</a></li>
                <li><a href="/case-studies-proof" className="hover:text-white transition-colors">Case Studies</a></li>
                <li><a href="/resources-hub" className="hover:text-white transition-colors">Resource Hub</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/about-ai-clean-core" className="hover:text-white transition-colors">About</a></li>
                <li><a href="/get-started" className="hover:text-white transition-colors">Get Started</a></li>
                <li><a href="/services-implementation" className="hover:text-white transition-colors">Services</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date()?.getFullYear()} AI Clean Core. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CaseStudiesProof;